import {
  afterRenderEffect,
  AfterViewInit,
  computed,
  Directive,
  ElementRef,
  inject,
  input,
  OnDestroy,
  Signal,
  signal,
} from '@angular/core';

import { watchSize } from '../utils/dom.utils';

/**
 * Directive that detects text truncation and provides a computed signal for conditional tooltip display.
 * Monitors element size changes and dynamically updates truncation state.
 *
 * Use with mibTooltip directive by binding to the tooltipText() signal:
 *
 * @example
 * ```html
 * <span
 *   class="truncate"
 *   [mibCheckTruncate]="tooltipContent"
 *   #check="checkTruncate"
 *   [mibTooltip]="check.tooltipText()">
 *   Some potentially long text that might be truncated
 * </span>
 * ```
 */
@Directive({
  selector: '[mibCheckTruncate]',
  exportAs: 'checkTruncate',
})
export class MibCheckTruncateDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private stopWatching?: () => void;
  private readonly isTruncated = signal(false);

  /**
   * The tooltip content to display when text is truncated.
   * If empty or undefined, no tooltip will be shown.
   */
  readonly content = input<string>('', { alias: 'mibCheckTruncate' });

  /**
   * Computed signal that returns the tooltip text only when content is truncated.
   * Use this signal with [mibTooltip] binding.
   */
  readonly tooltipText: Signal<string> = computed(() => {
    const truncated = this.isTruncated();
    const tooltipContent = this.content();
    return truncated && !!tooltipContent ? tooltipContent : '';
  });

  constructor() {
    // Re-check truncation whenever the bound content changes. ResizeObserver only fires on
    // element resize, so without this a content swap that fits in the same box (e.g. switching
    // from "America/Argentina/Buenos_Aires" to "UTC") would leave isTruncated stale.
    afterRenderEffect(() => {
      this.content();
      this.checkTruncation();
    });
  }

  /** Starts watching the element's size. */
  ngAfterViewInit(): void {
    this.setupResizeObserver();
  }

  /** Stops watching the element's size. */
  ngOnDestroy(): void {
    this.stopWatching?.();
  }

  /**
   * Watches the element's box so the truncation state follows a resize. Where the browser cannot
   * watch, the check made on every render is what keeps it right.
   */
  private setupResizeObserver(): void {
    this.stopWatching = watchSize(this.elementRef.nativeElement, () => this.checkTruncation());
  }

  /**
   * Checks if the element's text content is truncated by comparing
   * scrollWidth with offsetWidth. Updates the truncation signal.
   */
  private checkTruncation(): void {
    const element = this.elementRef.nativeElement;
    const truncated = element.scrollWidth > element.offsetWidth;
    this.isTruncated.set(truncated);
  }
}
